/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {EXCLUDED_FILTERS_LIST} from '../util/product-list-context'
import {useSortUrls} from '@salesforce/retail-react-app/app/hooks'

import {
    Stack,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import ColorRefinements from './color-refinements'
import SizeRefinements from './size-refinements'
import CheckboxRefinements from './checkbox-refinements'
import LinkRefinements from './link-refinements'
import RadioSortingOptions from './radio-sorting-options'

import {PlusIcon, MinusIcon} from '../../../components/custom-icons'
import {extendRefinementExclusions} from '../../../utils/plp-utils'
/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: LinkRefinements,
    c_subCategoryID: LinkRefinements,
    c_orliweb_color_code_rgt: ColorRefinements,
    c_size: SizeRefinements
}

/**
 * Refinements component for the product list page.
 *
 * This component is responsible for rendering and managing the available product filtering options,
 * such as categories, price ranges, brands, and other attributes. It allows users to refine the
 * displayed product list according to their preferences by selecting or deselecting various filters.
 *
 * Typically used as a sidebar or a collapsible panel, the Refinements component interacts with the
 * product list to update the displayed items in real-time based on the selected filters.
 *
 * @component
 * @example
 * <Refinements
 *   filters={filters}
 *   selectedFilters={selectedFilters}
 *   onFilterChange={handleFilterChange}
 * />
 */
const Refinements = ({
    isExcludedFilters = true,
    filters = [],
    sortingOptions = [],
    selectedFilters,
    setSelectedFilters,
    selectedSorting,
    setSelectedSorting,
    isLoading
}) => {
    const params = useParams()
    extendRefinementExclusions(params, EXCLUDED_FILTERS_LIST)

    const {formatMessage} = useIntl()

    // Get urls to be used for sorting
    const sortUrls = useSortUrls({options: sortingOptions})

    // Exclude filters in the exclude list.
    if (isExcludedFilters) {
        filters = filters.filter(({attributeId}) => !EXCLUDED_FILTERS_LIST.includes(attributeId))
    }

    // Getting the indixes of sorting + filters to open all accordion items by default
    let filtersIndexes = filters.map((_, idx) => idx)
    if (sortUrls) filtersIndexes.push(filters.length)

    return (
        <Stack
            as="section"
            aria-label={formatMessage({
                defaultMessage: 'Product filters',
                id: 'refinements.region.aria_label'
            })}
        >
            {/* Wait to have filters before rendering the Accordion to allow the default indexes to be accurate */}
            {/* Filter ADA: ul lists */}
            {filtersIndexes && (
                <Accordion
                    as="ul"
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    allowMultiple={true}
                    defaultIndex={filtersIndexes}
                    reduceMotion={true}
                >
                    {/* Sort By */}
                    {sortUrls && (
                        <AccordionItem
                            key="sort-by"
                            as="li"
                            listStyleType="none"
                            border="none"
                            padding={0}
                        >
                            {({isExpanded}) => (
                                <>
                                    {/* Using role="heading" instead of a semantic <h2> tag to avoid SEO impact */}
                                    <Text as="div" role="heading" aria-level="2">
                                        <AccordionButton
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={10}
                                            py={20}
                                        >
                                            <Text as="span" align="left" textStyle="bodyLarge2">
                                                {formatMessage({
                                                    defaultMessage: 'Sort by',
                                                    id: 'refinements.sort_by.title'
                                                })}
                                            </Text>
                                            {isExpanded ? (
                                                <MinusIcon boxSize="12px" />
                                            ) : (
                                                <PlusIcon boxSize="12px" />
                                            )}
                                        </AccordionButton>
                                    </Text>

                                    <AccordionPanel paddingTop={0} paddingBottom={10}>
                                        <RadioSortingOptions
                                            sortUrls={sortUrls}
                                            sortingOptions={sortingOptions}
                                            selectedSortingOptionLabel={selectedSorting}
                                            onSelectSorting={(value) => setSelectedSorting(value)}
                                        />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}

                    {filters?.map((filter) => {
                        // Render the appropriate component for the refinement type, fallback to checkboxes
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                        // Catch any non-array values and wrap them in an array
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        }

                        if (filter.values) {
                            return (
                                <AccordionItem
                                    key={`filter-accordion-item-${filter.attributeId}`}
                                    as="li"
                                    listStyleType="none"
                                    border="none"
                                    padding={0}
                                >
                                    {({isExpanded}) => (
                                        <>
                                            {/* Using role="heading" instead of a semantic <h2> tag to avoid SEO impact */}
                                            <Text as="div" role="heading" aria-level="2">
                                                <AccordionButton
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    gap={10}
                                                    py={20}
                                                >
                                                    <Text
                                                        as="span"
                                                        align="left"
                                                        textStyle="bodyLarge2"
                                                    >
                                                        {filter.label}
                                                    </Text>
                                                    {isExpanded ? (
                                                        <MinusIcon boxSize="12px" />
                                                    ) : (
                                                        <PlusIcon boxSize="12px" />
                                                    )}
                                                </AccordionButton>
                                            </Text>

                                            <AccordionPanel paddingTop={0} paddingBottom={10}>
                                                <Values
                                                    selectedFilters={selectedFiltersArray}
                                                    filter={filter}
                                                    onSelect={(value) => {
                                                        setSelectedFilters((prev) => {
                                                            const existing = normalizeRefinements(
                                                                prev[filter.attributeId]
                                                            )
                                                            const updated = [...existing, value]

                                                            const next = {...prev}
                                                            next[filter.attributeId] = updated
                                                            return next
                                                        })
                                                    }}
                                                    onDeselect={(value) => {
                                                        setSelectedFilters((prev) => {
                                                            const existing = normalizeRefinements(
                                                                prev[filter.attributeId]
                                                            )
                                                            const updated = existing.filter(
                                                                (v) => v !== value
                                                            )

                                                            const next = {...prev}
                                                            if (updated.length === 0) {
                                                                delete next[filter.attributeId]
                                                            } else {
                                                                next[filter.attributeId] = updated
                                                            }
                                                            return next
                                                        })
                                                    }}
                                                />
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            )
                        } else {
                            return null
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

/**
 * Normalizes the refinements data structure for use in the product list filtering UI.
 *
 * This function processes the input refinements, ensuring they are in a consistent format
 * suitable for rendering and further manipulation. It handles various input types and edge cases,
 * such as empty or malformed refinements, and returns a standardized array or object that can be
 * easily consumed by the UI components.
 *
 * @param {Array|Object} refinements - The raw refinements data to normalize, which may come from
 *   an API response or other dynamic sources.
 * @returns {Array|Object} The normalized refinements, structured for predictable use in the UI.
 */
const normalizeRefinements = (v) => (Array.isArray(v) ? v : v != null ? [v] : [])

Refinements.propTypes = {
    filters: PropTypes.array,
    sortingOptions: PropTypes.array,
    isExcludedFilters: PropTypes.bool,
    selectedFilters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    setSelectedFilters: PropTypes.func,
    selectedSorting: PropTypes.object,
    setSelectedSorting: PropTypes.func,
    isLoading: PropTypes.bool
}

export default Refinements
